import { useEffect, useState } from "react";

import { AccountRequestError, startTeamAppAccess } from "../../lib/account-client";
import { startGoogleSignIn } from "../../lib/auth-client";
import { posthog } from "../../lib/posthog";
import { DoorwayBrandIcon } from "../public/DoorwayBrandIcon";

type AccessState =
  | { status: "checking" }
  | { status: "sign-in-required" }
  | { status: "failed"; message: string }
  | { status: "incomplete" };

export function TeamAppAccessPage({ projectId, returnUrl }: { projectId: string | null; returnUrl: string | null }) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<AccessState>(projectId && returnUrl ? { status: "checking" } : { status: "incomplete" });
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signInPending, setSignInPending] = useState(false);

  useEffect(() => {
    if (!projectId || !returnUrl) {
      setState({ status: "incomplete" });
      return;
    }
    let active = true;
    setState({ status: "checking" });
    void startTeamAppAccess(projectId, returnUrl).then((result) => {
      if (active) window.location.assign(result.redirectUrl);
    }).catch((cause: unknown) => {
      if (!active) return;
      if (cause instanceof AccountRequestError && cause.status === 401) {
        setState({ status: "sign-in-required" });
        return;
      }
      setState({
        status: "failed",
        message: cause instanceof Error ? cause.message : "Doorway could not open this private app.",
      });
    });
    return () => { active = false; };
  }, [attempt, projectId, returnUrl]);

  const copy = teamAppAccessCopy(state);
  return (
    <div className="app-shell management-gate-shell">
      <header className="site-header management-gate-header">
        <a className="wordmark" href="/" aria-label="Doorway home">
          <DoorwayBrandIcon className="management-gate-logo" name="logo-tile-green" />
          Doorway
        </a>
        <span className="management-gate-security"><i aria-hidden="true" /> Secure handoff</span>
      </header>
      <main className="workspace-gate management-gate-main">
        <section
          className="flow-card management-gate management-gate-interstitial"
          data-state={state.status}
          aria-labelledby="team-app-access-heading"
        >
          <div className="management-gate-visual" aria-hidden="true">
            <span className="management-gate-cloud management-gate-cloud-one" />
            <span className="management-gate-cloud management-gate-cloud-two" />
            <span className="management-gate-route"><i /><i /><i /></span>
            <span className="management-gate-door-halo">
              <DoorwayBrandIcon className="management-gate-door" name="door-solid-green" />
            </span>
            <span className="management-gate-visual-label">Private, verified access</span>
          </div>
          <div className="management-gate-content">
            <div className="step-label">Private Team app</div>
            <h2 id="team-app-access-heading">{copy.heading}</h2>
            <p>{copy.description}</p>
            {state.status === "checking" && <span className="progress-orbit" aria-hidden="true" />}
            {state.status === "sign-in-required" && (
              <>
                <button
                  className="primary-button google-sign-in"
                  type="button"
                  disabled={signInPending}
                  onClick={() => {
                    if (signInPending) return;
                    setSignInError(null);
                    setSignInPending(true);
                    posthog.capture("sign_in_started", { provider: "google", source: "private_team_app" });
                    void startGoogleSignIn({ returnPath: `${window.location.pathname}${window.location.search}` }).catch((error: unknown) => {
                      setSignInPending(false);
                      setSignInError(error instanceof Error ? error.message : "Google sign-in could not start.");
                    });
                  }}
                >
                  <span className="google-logo" aria-hidden="true">
                    <svg viewBox="0 0 18 18" role="img">
                      <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.909c1.702-1.567 2.683-3.874 2.683-6.614Z" />
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.957-2.181l-2.909-2.258c-.806.54-1.835.86-3.048.86-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
                      <path fill="#FBBC05" d="M3.963 10.707A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.707V4.961H.956A9 9 0 0 0 0 9c0 1.45.347 2.824.956 4.039l3.007-2.332Z" />
                      <path fill="#EA4335" d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.463.892 11.426 0 9 0A9 9 0 0 0 .956 4.961l3.007 2.332C4.672 5.164 6.656 3.58 9 3.58Z" />
                    </svg>
                  </span>
                  {signInPending ? "Opening Google…" : "Continue with Google"}
                </button>
                {signInError && <p className="error-message" role="alert">{signInError}</p>}
              </>
            )}
            {state.status === "failed" && (
              <button className="secondary-button" type="button" onClick={() => setAttempt((current) => current + 1)}>
                Try again
              </button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function teamAppAccessCopy(state: AccessState): { heading: string; description: string } {
  switch (state.status) {
    case "checking":
      return {
        heading: "Opening your app",
        description: "Doorway is confirming your account and workspace access.",
      };
    case "sign-in-required":
      return {
        heading: "Sign in to open this app",
        description: "Use your Doorway account to continue to this private Team app.",
      };
    case "failed":
      return { heading: "We couldn't open this app", description: state.message };
    case "incomplete":
      return {
        heading: "This app link is incomplete",
        description: "Open the original private Team app link and try again.",
      };
  }
}
